import { Component, Inject, Input, NgZone, OnDestroy, Optional } from '@angular/core';
import { CommunicationUtilisateurDto } from './model/CommunicationUtilisateurDto';
import { CommunicationUtilisateurService } from './communication-utilisateur.service';
import { TranslateModule } from '@ngx-translate/core';
import { NotifierService } from '../../services/notifier.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { isNullOrUndefined } from '../../utils/functions';
import { AuthCoreService } from '../../services/auth-core.service';
import { AbstractSigaleComponent } from '../abstract-sigale.component';
import { StompService } from '../../services/stomp/stomp.service';
import { Observer } from '../../models/observer.interface';
import { Subscription } from 'rxjs';

@Component({
	standalone: true,
	selector: 'communication-utilisateur-component',
	templateUrl: './communication-utilisateur.component.html',
	styleUrls: ['./communication-utilisateur.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		TranslateModule
	]
})
export class CommunicationUtilisateurComponent extends AbstractSigaleComponent implements OnDestroy, Observer {

	@Input('doitAfficherCommunicationUtilisateur') doitAfficherCommunicationUtilisateur: () => boolean;
	// Le nom de l'évènement pour la notification de la websocket. À overrider si nécessaire.
	@Input('nomEvenementNotification') nomEvenementNotification = 'mettreAJourCommunicationUtilisateur';

	@Input() langue: string;
	communicationUtilisateur: CommunicationUtilisateurDto;
	messageDansLaLangueUtilisateur: string;

	private doitAfficherCommunicationUtilisateurInterne: boolean;
	private communicationAbonnement: Subscription;

	constructor(public communicationUtilisateurService: CommunicationUtilisateurService,
				@Optional() private notifierService: NotifierService, @Optional() private stompService: StompService,
				private ngZone: NgZone,
				private authCoreService: AuthCoreService,
				@Optional() @Inject('$scope') private $scope: any,
				@Optional() @Inject('$rootScope') private $rootScope: any) {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();

		if (this.stompService) {
			this.communicationAbonnement = this.stompService.abonnerTopic('communication-utilisateur', (communicationUtilisateurDto: CommunicationUtilisateurDto) =>
				this.ngZone.run(() => this.afficherMessage(communicationUtilisateurDto))
			);
			if (this.$scope) {
				this.$scope.$on('verifierCommunicationUtilisateur', () => this.recupererCommunicationUtilisateur());
			}
		}
		else {
			if (!this.modeApplicationCohabitation) {
				this.notifierService.subscribeToTopic(this, 'communication-utilisateur');
			}
			else if (this.$scope) {
				this.$scope.$on('verifierCommunicationUtilisateur', () => this.recupererCommunicationUtilisateur());
				this.$scope.$on(
					this.nomEvenementNotification, (event, nouvelleCommunication) => this.verifierDroitsEtMettreAJourCommunicationUtilisateur(
						nouvelleCommunication ?
							new CommunicationUtilisateurDto().deserialize(nouvelleCommunication) : nouvelleCommunication
					)
				);
			}
		}
	}

	/**
	 * Détermine si on doit afficher ou non la communication utilisateur
	 */
	determinerAffichageCommunicationUtilisateur(): boolean {
		// Si on a en @input du composant une fonction => on doit utiliser cette fonction pour déterminer l'affichage
		// sinon on utilise le booléen interne
		return this.doitAfficherCommunicationUtilisateur && this.doitAfficherCommunicationUtilisateur() ? this.doitAfficherCommunicationUtilisateur() : this.doitAfficherCommunicationUtilisateurInterne;
	}

	ngOnDestroy(): void {
		if (this.stompService) {
			this.stompService.desabonner(this.communicationAbonnement);
		}
		else if (!this.modeApplicationCohabitation) {
			this.notifierService.disconnectFromTopic('communication-utilisateur');
		}
	}

	public notify(response: any): void {
		if (!this.modeApplicationCohabitation) {
			this.ngZone.run(() => {
				const message = JSON.parse(response).data;
				this.afficherMessage(message);
			});
		}
	}

	private afficherMessage(message: CommunicationUtilisateurDto) {
		if (this.determinerAffichageCommunicationUtilisateur()) {
			this.mettreAJourCommunicationUtilisateur(
				message ? new CommunicationUtilisateurDto().deserialize(message) : message
			);
		}
		else {
			this.communicationUtilisateur = undefined;
		}

	}

	/**
	 * Met à jour la communication.
	 *
	 * @param nouvelleCommunication La nouvelle communication.
	 */
	mettreAJourCommunicationUtilisateur(nouvelleCommunication: CommunicationUtilisateurDto): void {
		this.communicationUtilisateur = nouvelleCommunication;
		this.determinerMessageDeLaLangueUtilisateur();
	}

	verifierDroitsEtMettreAJourCommunicationUtilisateur(nouvelleCommunication: CommunicationUtilisateurDto): void {
		if (this.determinerAffichageCommunicationUtilisateur()) {
			this.mettreAJourCommunicationUtilisateur(nouvelleCommunication);
		}
		else {
			this.communicationUtilisateur = undefined;
		}
	}

	/**
	 * Récupère la dernière communication utilisateur si on peut l'afficher.
	 */
	recupererCommunicationUtilisateur(): void {
		if (this.determinerAffichageCommunicationUtilisateur()) {
			this.communicationUtilisateurService.recupererCommunicationUtilisateur().subscribe(data => {
				this.mettreAJourCommunicationUtilisateur(data);
			});
		}
		else {
			this.communicationUtilisateur = undefined;
		}
	}

	/**
	 * Permet de déterminer le message du magasin selon la langue de l'utilisateur connecté
	 */
	determinerMessageDeLaLangueUtilisateur(): void {
		if (!isNullOrUndefined(this.communicationUtilisateur)) {
			let langueUtilisateur;
			if (this.langue) {
				langueUtilisateur = this.langue;
			}
			else if (this.$rootScope) {
				langueUtilisateur = this.$rootScope.langue;
			}
			else {
				langueUtilisateur = this.authCoreService.determinerLangueDepuisToken();
			}
			let messageParLangue = this.communicationUtilisateur.messageParLangue.find(communications => communications.langue.code === langueUtilisateur);

			if (isNullOrUndefined(messageParLangue)) {
				messageParLangue = this.communicationUtilisateur.messageParLangue.find(communications => communications.estFrancais());
			}

			this.messageDansLaLangueUtilisateur = messageParLangue.message;
		}
		else if (!this.modeApplicationCohabitation) {
			this.messageDansLaLangueUtilisateur = undefined;
		}
	}

	/**
	 * Met à jour la condition d'affichage.
	 *
	 * @param doitAfficherCommunicationUtilisateur vrai s'il faut afficher la communication utilisateur.
	 */
	mettreAJourConditionAffichage(doitAfficherCommunicationUtilisateur: boolean) {
		this.doitAfficherCommunicationUtilisateurInterne = doitAfficherCommunicationUtilisateur;
	}
}
